import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import RadioGroup from '@cloudscape-design/components/radio-group';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { TenantRole } from '@etiquetachef/types';
import { toast } from '@/lib/notifications';
import {
  createStaffWithCredentialsCallable,
  inviteUserToTenantCallable,
} from '@/lib/callables';

type Props = {
  open: boolean;
  tenantId: string;
  /** País canônico do tenant (`tenant.countryCode`) — trava o telefone. */
  tenantCountry: string | null;
  onClose: () => void;
};

const PHONE_RE = /^\+\d{8,20}$/;
const USERNAME_RE = /^[a-z0-9](?:[a-z0-9]|[._-](?![._-])){0,30}[a-z0-9]$/;
const PIN_RE = /^\d{6}$/;

/**
 * Adiciona um usuário ao tenant pelo painel super-admin. OWNER/MANAGER são
 * convidados por email (`inviteUserToTenant`, telefone travado ao país do
 * tenant); STAFF é criado com username + PIN (`createStaffWithCredentials`).
 *
 * Origem: sem issue — commit f948c64e4, 2026-06-10 18:39 +0200 (abas
 * Usuários e Responsáveis na página do tenant).
 *
 * Contrato: valida no cliente só o formato (nome ≥ 2, e-mail, `+` e 8–20
 * dígitos; username `USERNAME_RE` e PIN de 6 dígitos para STAFF). O membro
 * aparece na lista pelo listener de `members`, sem refetch.
 *
 * Usado por: `features/members/MembersTab.tsx`.
 *
 * Arquitetura: a trava de país do telefone é do servidor —
 * `inviteUserToTenant` recusa com `invalid-argument` quando
 * `resolveSupportedCountry(phoneE164) ≠ tenant.countryCode` (CLAUDE.md
 * "Telefone — só do país do tenant"). Nesta UI o país aparece só no texto de
 * ajuda: o campo é livre, sem o DDI fixo que a regra pede (ver #926, achados).
 *
 * Dados — inputs (schemas em `@etiquetachef/types`):
 *   inviteUserToTenant: { "tenantId": "tenant-1", "displayName": "Fulana",
 *     "email": "fulana@example.com", "phoneE164": "+5511900000000",
 *     "role": "MANAGER" }
 *   createStaffWithCredentials: { "tenantId": "tenant-1",
 *     "username": "cozinha.1", "pin": "000000", "displayName"?: "Cozinha" }
 *
 * Falhas conhecidas:
 *  - o tratamento de erro procura o código (`'already-exists'`) dentro de
 *    `err.message`, mas o `FirebaseError` do SDK de Functions traz em
 *    `message` o texto do servidor (ex.: "Usuário já é membro do estabelecimento.") e o código em `err.code`
 *    (`functions/already-exists`). A mensagem específica nunca aparece; o operador vê
 *    sempre a genérica (ver #926, achados).
 *  - O `catch` só faz `console.error` além do erro na tela — sem report.
 */
export function MemberAddDialog({ open, tenantId, tenantCountry, onClose }: Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const [role, setRole] = useState<TenantRole>(TenantRole.MANAGER);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('+');
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setRole(TenantRole.MANAGER);
    setDisplayName('');
    setEmail('');
    setPhone('+');
    setUsername('');
    setPin('');
    setError(null);
  }, [open]);

  if (!open) return null;

  const isStaff = role === TenantRole.STAFF;
  const canSubmit = isStaff
    ? USERNAME_RE.test(username) && PIN_RE.test(pin)
    : displayName.trim().length >= 2 && /.+@.+\..+/.test(email) && PHONE_RE.test(phone);

  function close(): void {
    setError(null);
    onClose();
  }

  async function submit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      if (isStaff) {
        await createStaffWithCredentialsCallable({
          tenantId,
          username,
          pin,
          ...(displayName.trim().length > 0 ? { displayName: displayName.trim() } : {}),
        });
      } else {
        await inviteUserToTenantCallable({
          tenantId,
          displayName: displayName.trim(),
          email: email.trim(),
          phoneE164: phone.trim(),
          role,
        });
      }
      toast.success(t('adminMembers.toast.added'));
      close();
    } catch (err) {
      const code = err instanceof Error ? err.message : String(err);
      if (code.includes('already-exists')) {
        setError(t('adminMembers.errors.alreadyExists'));
      } else if (code.includes('invalid-argument')) {
        setError(t('adminMembers.errors.phoneCountry', { country: tenantCountry ?? '' }));
      } else {
        setError(t('adminMembers.errors.addFailed'));
      }
      console.error('memberAdd submit failed', err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      onDismiss={close}
      header={t('adminMembers.addDialog.title')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {t('adminMembers.addDialog.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void submit();
              }}
              loading={submitting}
              disabled={!canSubmit}
            >
              {t('adminMembers.addDialog.submit')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <FormField label={t('adminMembers.addDialog.role')}>
          <RadioGroup
            value={role}
            onChange={({ detail }): void => {
              setRole(detail.value as TenantRole);
            }}
            items={[
              { value: TenantRole.OWNER, label: t('adminMembers.roles.OWNER') },
              { value: TenantRole.MANAGER, label: t('adminMembers.roles.MANAGER') },
              {
                value: TenantRole.STAFF,
                label: t('adminMembers.roles.STAFF'),
                description: t('adminMembers.addDialog.staffHint'),
              },
            ]}
          />
        </FormField>

        {isStaff ? (
          <>
            <FormField label={t('adminMembers.addDialog.username')} description={t('adminMembers.addDialog.usernameHint')}>
              <Input
                value={username}
                onChange={({ detail }): void => {
                  setUsername(detail.value.toLowerCase());
                }}
                disabled={submitting}
                invalid={username.length > 0 && !USERNAME_RE.test(username)}
                placeholder="maria.silva"
              />
            </FormField>
            <FormField label={t('adminMembers.addDialog.pin')} description={t('adminMembers.addDialog.pinHint')}>
              <Input
                value={pin}
                onChange={({ detail }): void => {
                  setPin(detail.value.replace(/\D/g, '').slice(0, 6));
                }}
                disabled={submitting}
                invalid={pin.length > 0 && !PIN_RE.test(pin)}
                inputMode="numeric"
                placeholder="000000"
              />
            </FormField>
            <FormField label={t('adminMembers.addDialog.displayNameOptional')}>
              <Input
                value={displayName}
                onChange={({ detail }): void => {
                  setDisplayName(detail.value);
                }}
                disabled={submitting}
              />
            </FormField>
          </>
        ) : (
          <>
            <FormField label={t('adminMembers.addDialog.name')}>
              <Input
                value={displayName}
                onChange={({ detail }): void => {
                  setDisplayName(detail.value);
                }}
                disabled={submitting}
              />
            </FormField>
            <FormField label={t('adminMembers.addDialog.email')}>
              <Input
                value={email}
                onChange={({ detail }): void => {
                  setEmail(detail.value);
                }}
                disabled={submitting}
                type="email"
                placeholder="pessoa@email.com"
              />
            </FormField>
            <FormField
              label={t('adminMembers.addDialog.phone')}
              description={t('adminMembers.addDialog.phoneHint', { country: tenantCountry ?? '—' })}
            >
              <Input
                value={phone}
                onChange={({ detail }): void => {
                  setPhone(detail.value.replace(/[^\d+]/g, ''));
                }}
                disabled={submitting}
                invalid={phone.length > 1 && !PHONE_RE.test(phone)}
                placeholder="+55 11 99999 9999"
              />
            </FormField>
          </>
        )}

        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
